import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { API } from "@/lib/api";
import {
  CheckCircle2, Loader2, AlertTriangle, Upload, FileText, Calendar, Car as CarIcon, Camera,
} from "lucide-react";
import MovixLogo from "@/components/MovixLogo";

/**
 * Public self check-in form (no auth, token-based).
 * Customer fills in personal data + uploads driver license photo BEFORE arriving
 * at the shop, so the desk visit is short.
 */
export default function SelfCheckin() {
  const { token } = useParams();
  const fileRef = useRef(null);
  const [info, setInfo] = useState(null);
  const [state, setState] = useState({ status: "loading", error: null });
  const [form, setForm] = useState({
    name: "", social_security: "", driver_license: "",
    phone: "", email: "", address: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/public/checkin/${token}`, { credentials: "omit" });
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          setState({
            status: "error",
            error: res.status === 410 ? "expired" : res.status === 404 ? "invalid" : j.detail || "error",
          });
          return;
        }
        const data = await res.json();
        setInfo(data);
        setForm((prev) => ({
          ...prev,
          name: data.prefill?.name || "",
          phone: data.prefill?.phone || "",
          email: data.prefill?.email || "",
        }));
        if (data.submitted_at) setSubmitted(true);
        setState({ status: "ok", error: null });
      } catch (e) {
        setState({ status: "error", error: String(e.message || e) });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/public/checkin/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "omit",
        body: JSON.stringify(form),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.detail || "error");
      }
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      alert(String(err.message || err));
    } finally {
      setSubmitting(false);
    }
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    setUploading(true);
    for (const f of list) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("label", "driver_license");
        const res = await fetch(`${API}/public/checkin/${token}/upload`, {
          method: "POST",
          credentials: "omit",
          body: fd,
        });
        if (res.ok) {
          setUploads((u) => [...u, f.name]);
        }
      } catch { /* ignore */ }
    }
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  if (state.status === "loading") {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#F5A623] animate-spin" />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="min-h-screen bg-zinc-100 flex items-center justify-center p-6">
        <div className="border-2 border-red-200 bg-red-50 p-10 text-center max-w-md">
          <AlertTriangle className="w-12 h-12 mx-auto text-red-500 mb-4" />
          <div className="font-display font-bold text-2xl tracking-tight mb-2">
            {state.error === "expired" ? "Link expired" : state.error === "invalid" ? "Invalid link" : "Could not load"}
          </div>
          <p className="text-sm text-zinc-600">Please request a new check-in link from Movix.</p>
        </div>
      </div>
    );
  }

  const car = info.car || {};
  const rental = info.rental || {};

  return (
    <div className="min-h-screen bg-zinc-100 flex flex-col" data-testid="self-checkin-page">
      <header className="bg-[#0A0A0A] text-white">
        <div className="max-w-2xl mx-auto px-6 py-4 flex items-center justify-between">
          <MovixLogo variant="light" size="sm" showTagline={false} />
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Self check-in</div>
        </div>
      </header>

      <main className="flex-1 max-w-2xl mx-auto w-full p-4 md:p-6 space-y-5">
        {/* Rental info card */}
        <div className="bg-white border border-zinc-200 p-5">
          <div className="overline mb-2">Your reservation</div>
          <div className="font-display font-black text-2xl tracking-tighter mb-2">
            {car.model} · {car.plate}
          </div>
          <div className="grid grid-cols-2 gap-3 text-xs text-zinc-600">
            <div>
              <div className="overline text-[9px]">Pickup</div>
              <div className="font-mono">{rental.pickup_date ? new Date(rental.pickup_date).toLocaleString() : "—"}</div>
            </div>
            <div>
              <div className="overline text-[9px]">Return</div>
              <div className="font-mono">{rental.return_date ? new Date(rental.return_date).toLocaleString() : "—"}</div>
            </div>
          </div>
        </div>

        {submitted ? (
          <div className="border-2 border-[#F5A623] bg-orange-50/40 p-6 text-center" data-testid="checkin-success">
            <CheckCircle2 className="w-12 h-12 mx-auto text-[#F5A623] mb-3" />
            <div className="font-display font-bold text-xl tracking-tight">All set! 🎉</div>
            <p className="text-sm text-zinc-600 mt-2 max-w-md mx-auto">
              We've received your information. When you arrive at Movix to pick up your car, we'll only need a quick ID check and your signature.
            </p>
            <div className="mt-4 pt-4 border-t border-[#F5A623]/30 text-xs text-zinc-500">
              <FileText className="w-3 h-3 inline mr-1" /> Please bring your physical Driver License to pickup
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4 bg-white border border-zinc-200 p-5">
            <div>
              <h2 className="font-display font-bold text-xl tracking-tight mb-1">Your details</h2>
              <p className="text-xs text-zinc-500">
                Complete this 2-minute form before pickup. Everything is encrypted and used only for the rental contract.
              </p>
            </div>

            <Field label="Full name" required>
              <input
                required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-3 text-base focus:border-[#F5A623] focus:outline-none"
                data-testid="checkin-name"
                placeholder="As shown on your driver license"
              />
            </Field>

            <Field label="Social Security (SSN)" required>
              <input
                required value={form.social_security}
                onChange={(e) => setForm({ ...form, social_security: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-3 text-base font-mono focus:border-[#F5A623] focus:outline-none"
                data-testid="checkin-ssn"
                placeholder="XXX-XX-XXXX"
              />
            </Field>

            <Field label="Driver License #" required>
              <input
                required value={form.driver_license}
                onChange={(e) => setForm({ ...form, driver_license: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-3 text-base font-mono focus:border-[#F5A623] focus:outline-none"
                data-testid="checkin-license"
              />
            </Field>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Phone" required>
                <input
                  required value={form.phone} type="tel"
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-3 text-base focus:border-[#F5A623] focus:outline-none"
                  data-testid="checkin-phone"
                />
              </Field>
              <Field label="Email">
                <input
                  value={form.email} type="email"
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full border border-zinc-300 px-3 py-3 text-base focus:border-[#F5A623] focus:outline-none"
                  data-testid="checkin-email"
                />
              </Field>
            </div>

            <Field label="Address">
              <input
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                className="w-full border border-zinc-300 px-3 py-3 text-base focus:border-[#F5A623] focus:outline-none"
                data-testid="checkin-address"
                placeholder="Street, city, state"
              />
            </Field>

            {/* Driver license photo upload */}
            <Field label="Photo of Driver License (front)">
              <div className="border-2 border-dashed border-zinc-300 p-4 text-center bg-zinc-50/50">
                <Camera className="w-8 h-8 mx-auto text-zinc-400 mb-2" />
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  multiple
                  onChange={(e) => handleFiles(e.target.files)}
                  className="hidden"
                  data-testid="checkin-file-input"
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="inline-flex items-center gap-2 bg-black hover:bg-[#F5A623] text-white px-4 py-2 text-sm transition-colors"
                  data-testid="checkin-upload-btn"
                >
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Uploading..." : "Choose / take photo"}
                </button>
                {uploads.length > 0 && (
                  <div className="mt-3 text-xs text-green-700 font-mono">
                    <CheckCircle2 className="w-3 h-3 inline mr-1" /> {uploads.length} file(s) uploaded
                  </div>
                )}
              </div>
            </Field>

            <button
              type="submit"
              disabled={submitting}
              className="w-full bg-[#F5A623] hover:bg-[#D97706] text-white font-bold tracking-tight py-4 text-base disabled:opacity-50 transition-colors"
              data-testid="checkin-submit"
            >
              {submitting ? <Loader2 className="w-5 h-5 inline animate-spin mr-2" /> : <CheckCircle2 className="w-5 h-5 inline mr-2" />}
              Submit check-in
            </button>

            <p className="text-[10px] text-zinc-500 text-center pt-2 border-t border-zinc-100">
              🔒 Your data is securely stored and only visible to Movix staff. By submitting you agree to the Movix rental terms.
            </p>
          </form>
        )}
      </main>

      <footer className="bg-zinc-900 text-zinc-400 text-xs py-4 mt-6">
        <div className="max-w-2xl mx-auto px-6 flex items-center justify-between flex-wrap gap-2">
          <div>© Movix Car Rental · Boston, Massachusetts</div>
          <div>(561) 455-6277</div>
        </div>
      </footer>
    </div>
  );
}

const Field = ({ label, required, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs uppercase tracking-wider text-zinc-600 font-mono">
      {label}{required && <span className="text-[#F5A623]"> *</span>}
    </span>
    {children}
  </label>
);
